import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { interviewRuntimeAPI } from '../api/interviewRuntimeAPI';
import InterviewUpgradeGate, { useGateStatus } from './InterviewUpgradeGate';
import { holdsPaidExam } from '../../quiz-generation/components/LockedPartNotice';
import { clearSittingState, liveSlot, sittingProgress } from '../sittingStorage';
import { timingText } from '../timing';
import {
  clearDraft, clearResult, loadResult, writtenProgress,
} from '../writtenStorage';
import './InterviewBrowse.css';

/**
 * One course: every interview it offers, laid out to choose from. Live
 * interviews are spoken, each a whole interview in the school's own format
 * (its stations in order, or an Oxbridge interview's questions). Mock
 * interview papers are written, and an Oxbridge paper's interviews (two, or
 * three for Oxford PPE) are listed one by one, so one item is one interview.
 * Each shows whether this student has completed it, with its feedback and
 * review a click away.
 */
function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

/** A live interview's open run on this account, from the server (a second
 * device, or cleared storage, has no local progress). */
function hasOpenRun(interview) {
  return Object.values(interview.runs || {}).some((run) => run && run.is_complete === false);
}

function Status({
  completed, completedAt, inProgress, onStart, onResume, onFeedback, onReview, onAgain, startLabel,
}) {
  if (completed) {
    return (
      <div className="ib__item-actions">
        <span className="ib__done">Completed{completedAt ? ` · ${formatDate(completedAt)}` : ''}</span>
        <button type="button" className="ib__btn" onClick={onFeedback}>Feedback</button>
        <button type="button" className="ib__btn ib__btn--ghost" onClick={onReview}>Review</button>
        {onAgain && <button type="button" className="ib__link-btn" onClick={onAgain}>Take it again</button>}
      </div>
    );
  }
  if (inProgress) {
    return (
      <div className="ib__item-actions">
        <button type="button" className="ib__btn" onClick={onResume}>Resume</button>
        {onAgain && <button type="button" className="ib__link-btn" onClick={onAgain}>Start again</button>}
      </div>
    );
  }
  return (
    <div className="ib__item-actions">
      <button type="button" className="ib__btn" onClick={onStart}>{startLabel}</button>
    </div>
  );
}

export default function InterviewPapersPage() {
  const { workspace: workspaceSlug, leafKey } = useParams();
  // Saved progress is the signed-in user's own; without an id there is none.
  const { user } = useAuth();
  const userId = user?.id || null;
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [gateCode, statusFor] = useGateStatus();
  const [data, setData] = useState(null);
  const [, setRefresh] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    interviewRuntimeAPI.getLeafOverview(leafKey)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus(statusFor(err));
      });
    return () => { cancelled = true; };
  }, [leafKey]);

  if (status === 'loading') return <div className="ib"><div className="ib__state">Loading…</div></div>;
  if (status === 'upgrade') return <div className="ib"><InterviewUpgradeGate code={gateCode} paid={holdsPaidExam(user)} /></div>;
  if (status === 'error' || !data?.leaf) {
    return (
      <div className="ib">
        <div className="ib__state ib__state--error">
          Could not load this course. <Link to={`/interviews/${workspaceSlug}`}>Back</Link>
        </div>
      </div>
    );
  }

  const { leaf, live = [], papers = [], timing = null, voices = [] } = data;
  const courseUrl = `/interviews/${workspaceSlug}/${leafKey}`;
  const oxbridge = leaf.workspace === 'Oxbridge';
  const liveCount = live.length;
  // A Beginnings-only account gets one free live interview per package: the
  // server marks the claimed one and the ones that can no longer be started,
  // and only a Beginnings-only account has either. `has_paid === false` covers
  // the account that has not yet started its free interview.
  const beginningsOnly = user?.access?.has_paid === false
    || live.some((i) => i.claimed === true || i.live_mock_used === true);
  const anyClaimed = live.some((i) => i.claimed === true);
  const paperInterviews = papers.reduce((n, p) => n + p.interviews.length, 0);

  // ---- live interviews ------------------------------------------------------
  const liveUrl = (i) => `${courseUrl}/interview/${i.set_id}`;
  const startLive = (i) => {
    const [paperId, sessionId] = liveSlot(leafKey, i.set_id);
    clearSittingState(userId, paperId, sessionId);
    navigate(liveUrl(i), {
      state: {
        stations: i.stations, leaf, minutes: i.minutes, timing, avatar: i.avatar, examiners: i.examiners, voices,
      },
    });
  };
  // Resume carries no stations: the sitting page finds its progress in storage
  // or, on a device that has none, from the runs the server holds.
  const resumeLive = (i) => navigate(liveUrl(i));
  const liveFeedback = (i, page) => navigate(`${courseUrl}/live/${page}`, { state: { runIds: i.run_ids, mode: 'live' } });

  // ---- mock interview papers -------------------------------------------------
  const writeUrl = (p, s) => `${courseUrl}/${p.paper_id}/write/${s.session_id}`;
  const paperRuns = (p, s) => (s.completed ? s.run_ids : (loadResult(userId, p.paper_id, s.session_id)?.runIds || []));
  const paperFeedback = (p, s, page) => navigate(`${courseUrl}/${p.paper_id}/${page}`, {
    state: { runIds: paperRuns(p, s), mode: 'written' },
  });
  const paperAgain = (p, s) => {
    clearDraft(userId, p.paper_id, s.session_id);
    clearResult(userId, p.paper_id, s.session_id);
    setRefresh((n) => n + 1);
    navigate(writeUrl(p, s), { state: { minutes: s.minutes } });
  };

  return (
    <div className="ib">
      <div className="ib__crumbs">
        <Link to="/apps">Apps</Link> / <Link to={`/interviews/${workspaceSlug}`}>{leaf.workspace || workspaceSlug}</Link> / {leaf.display_name}
      </div>
      <h1 className="ib__title">{leaf.display_name}</h1>

      <h2 className="ib__section-title">Live interviews</h2>
      <p className="ib__lede">
        {liveCount
          ? `${liveCount} interview${liveCount === 1 ? '' : 's'}${oxbridge ? '' : `, each a full set of ${leaf.institution}'s stations`}. The examiner asks the questions aloud and follows up on your answers. ${timingText(timing, leaf.institution)}`.trim()
          : 'No live interviews here yet.'}
      </p>
      <div className="ib__list">
        {live.map((i) => {
          const [paperId, sessionId] = liveSlot(leafKey, i.set_id);
          const inProgress = sittingProgress(userId, paperId, sessionId) === 'in-progress' || hasOpenRun(i);
          const used = i.live_mock_used === true;
          const locked = i.locked === true;
          // The free interview: the claimed one, or with none claimed every
          // interview that can still be started.
          const free = beginningsOnly && !used && !locked && (i.claimed === true || !anyClaimed);
          return (
            <div className={`ib__item${i.completed ? ' ib__item--done' : ''}`} key={i.set_id}>
              <div className="ib__item-info">
                <span className="ib__item-title">
                  Interview {i.interview_number}
                  {i.harder && <span className="ib__tag">Harder</span>}
                  {free && <span className="ib__tag">Your free interview</span>}
                </span>
                <span className="ib__item-meta">
                  {oxbridge
                    ? `${i.stations.length} question${i.stations.length === 1 ? '' : 's'}`
                    : `${i.stations.length} station${i.stations.length === 1 ? '' : 's'}`}
                  {i.minutes ? ` · ${i.minutes} min` : ''}
                </span>
                <ol className="ib__stations">
                  {/* Two questions on one topic show it once. */}
                  {i.stations.map((st) => st.topic || st.label)
                    .filter((label, n, all) => all.indexOf(label) === n)
                    .map((label) => <li key={label}>{label}</li>)}
                </ol>
              </div>
              {locked && (
                <div className="ib__item-actions">
                  <span className="ib__done">This interview is locked.</span>
                  <Link className="ib__link-btn" to="/pricing">See plans</Link>
                </div>
              )}
              {!locked && used && (
                <div className="ib__item-actions">
                  <span className="ib__done">Your free interview has been used.</span>
                </div>
              )}
              {!locked && !used && (
                <Status
                  completed={i.completed}
                  completedAt={i.completed_at}
                  inProgress={inProgress}
                  startLabel="Start interview"
                  onStart={() => startLive(i)}
                  onResume={() => resumeLive(i)}
                  onFeedback={() => liveFeedback(i, 'feedback')}
                  onReview={() => liveFeedback(i, 'review')}
                  /* Every question of a Beginnings-only account's interview
                     already has a run, so a retake would be refused. */
                  onAgain={beginningsOnly ? null : () => startLive(i)}
                />
              )}
            </div>
          );
        })}
      </div>

      <h2 className="ib__section-title">Mock interview papers</h2>
      <p className="ib__lede">
        {paperInterviews
          ? `${paperInterviews} written interview${paperInterviews === 1 ? '' : 's'}. You type your answers and each one is marked as you go.`
          : 'No mock interview papers here yet.'}
      </p>
      <div className="ib__list">
        {papers.flatMap((p) => p.interviews.map((s) => {
          const local = writtenProgress(userId, p.paper_id, s.session_id);
          const completed = s.completed || local === 'marked';
          const several = p.interviews.length > 1;
          return (
            <div className={`ib__item${completed ? ' ib__item--done' : ''}`} key={s.session_id}>
              <div className="ib__item-info">
                <span className="ib__item-title">
                  {p.title}{several ? ` · ${s.title || `Interview ${s.session_number}`}` : ''}
                </span>
                <span className="ib__item-meta">
                  {s.question_count} question{s.question_count === 1 ? '' : 's'}
                  {s.minutes ? ` · ${s.minutes} min` : ''}
                </span>
                {s.focus && <span className="ib__session-focus">{s.focus}</span>}
              </div>
              {p.locked === true ? (
                <div className="ib__item-actions">
                  <span className="ib__done">This paper is locked.</span>
                  <Link className="ib__link-btn" to="/pricing">See plans</Link>
                </div>
              ) : (
              <Status
                completed={completed}
                completedAt={s.completed_at}
                inProgress={local === 'draft'}
                startLabel="Start written paper"
                onStart={() => navigate(writeUrl(p, s), { state: { minutes: s.minutes } })}
                onResume={() => navigate(writeUrl(p, s), { state: { minutes: s.minutes } })}
                onFeedback={() => paperFeedback(p, s, 'feedback')}
                onReview={() => paperFeedback(p, s, 'review')}
                onAgain={() => paperAgain(p, s)}
              />
              )}
            </div>
          );
        }))}
      </div>
    </div>
  );
}
